import { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "বিয়ে", "বাড়ির অনুষ্ঠান", "পুজো", "পাড়ার অনুষ্ঠান", "স্কুল-কলেজ",
  "কর্পোরেট", "মেলা-উৎসব", "নিজের অনুষ্ঠান", "অন্য কিছু",
];
const DURATIONS = ["১ ঘণ্টা", "২ ঘণ্টা", "৩ ঘণ্টা", "পুরো অনুষ্ঠান (৪+ ঘণ্টা)"];
const AUDIENCES = ["৫০-র নিচে", "৫০–২০০", "২০০–৫০০", "৫০০-র উপরে"];
const BUDGETS = [
  { label: "₹৫,০০০ – ₹৮,০০০", min: 5000, max: 8000 },
  { label: "₹৮,০০০ – ₹১২,০০০", min: 8000, max: 12000 },
  { label: "₹১২,০০০ – ₹২০,০০০", min: 12000, max: 20000 },
  { label: "₹২০,০০০-র উপরে", min: 20000, max: 40000 },
];
const STEP_TITLES = [
  "আপনার অনুষ্ঠান কী?", "অনুষ্ঠানের তারিখ", "অনুষ্ঠানের সময়", "কোথায় হবে?",
  "কতক্ষণের পরিবেশনা?", "দর্শক কতজন হবেন?", "বাজেট-ধারা", "শেষ কথা — আপনার পরিচয়",
];

const bn = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

export default function BookingWizard({ artist, onClose, onHostLine }) {
  const [step, setStep] = useState(0);
  const [d, setD] = useState({
    event_type: "", date: "", time: "", location: "", duration: "২ ঘণ্টা",
    audience: "", budget_min: 0, budget_max: 0, note: "", name: "", phone: "",
  });
  const [checking, setChecking] = useState(false);
  const [avail, setAvail] = useState(null);
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState(null); // শিল্পীর জবাব (দর-কথা চলাকালীন)
  const [offer, setOffer] = useState("");
  const [offerOpen, setOfferOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [waitingArtist, setWaitingArtist] = useState(false);

  const pick = (k, v) => {
    setD((x) => ({ ...x, [k]: v }));
    setStep((s) => Math.min(7, s + 1));
  };

  const canNext =
    [Boolean(d.event_type), Boolean(d.date), Boolean(d.time), Boolean(d.location.trim()),
     Boolean(d.duration), Boolean(d.audience), d.budget_max > 0, Boolean(d.name.trim() && d.phone.trim())][step];

  const checkAvailability = async () => {
    setChecking(true);
    setError("");
    try {
      const r = await fetch(`${API}/moncho/artists/${artist.id}/availability`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: d.date, time: d.time }),
      });
      setAvail(await r.json());
      setStep(8);
    } catch {
      setError("যাচাই হলো না — আবার চাপুন।");
    }
    setChecking(false);
  };

  const sendRequest = async () => {
    setSending(true);
    setError("");
    try {
      const r = await fetch(`${API}/moncho/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...d, artist_id: artist.id }),
      });
      const doc = await r.json();
      if (!r.ok || !doc.ok) throw new Error(doc.detail || "অনুরোধ যায়নি — আবার চেষ্টা করুন");
      setReply({ ...(doc.artist_reply || {}), code: doc.code, artist_name: doc.artist_name });
      // শিল্পীর জবাব আসছে — প্রদীপের ছলছলে
      setWaitingArtist(true);
      setTimeout(() => setWaitingArtist(false), 2400);
      if (onHostLine) onHostLine("অনুরোধ পৌঁছেছে — শিল্পীর খবর অপেক্ষা করুন, এই তো আসছে।");
    } catch (e) {
      setError(e.message || "অনুরোধ যায়নি — আবার চেষ্টা করুন");
    }
    setSending(false);
  };

  const respond = async (action, offerVal = 0) => {
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/moncho/booking/${encodeURIComponent(reply.code)}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, offer: offerVal }),
      });
      const doc = await r.json();
      if (!r.ok || !doc.ok) throw new Error(doc.detail || "যোগাযোগ হলো না — আবার চাপুন");
      setWaitingArtist(true);
      setTimeout(() => setWaitingArtist(false), 2000);
      setReply({ ...doc, code: reply.code, artist_name: artist.name });
      setOfferOpen(false);
      setOffer("");
    } catch (e) {
      setError(e.message || "যোগাযোগ হলো না — আবার চাপুন");
    }
    setBusy(false);
  };

  const confirmBooking = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/moncho/booking/${encodeURIComponent(reply.code)}/confirm`, { method: "POST" });
      const doc = await r.json();
      if (!r.ok || !doc.ok) throw new Error(doc.detail || "কনফার্ম হলো না");
      setReceipt(doc);
      if (onHostLine) onHostLine("বুকিং নিশ্চিত! শিল্পী আপনার অনুষ্ঠানে আসবেন — ঠিকানা-কথা ফোনে মিলবে।");
    } catch (e) {
      setError(e.message || "কনফার্ম হলো না — আবার চাপুন");
    }
    setBusy(false);
  };

  const chipCls = (on) =>
    `rounded-full border px-4 py-2.5 text-sm transition-all duration-300 ${
      on ? "border-gold bg-gold/20 text-gold" : "border-sand/25 text-sand/80 hover:border-gold/60 hover:text-gold"
    }`;

  return (
    <motion.div
      className="fixed inset-0 z-[91] flex items-center justify-center bg-dusk/92 p-3 backdrop-blur-md sm:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="booking-wizard"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[94vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-gold/25 bg-plaster p-6 sm:p-8"
      >
        <button
          data-testid="booking-wizard-close"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full bg-dusk/70 p-2 text-sand/70 transition-colors hover:text-sand"
          aria-label="বন্ধ করুন"
        >
          <X size={16} />
        </button>

        {/* ---------- রসিদ ---------- */}
        {receipt ? (
          <div className="py-6 text-center" data-testid="booking-receipt">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
              <span className="h-6 w-1.5 animate-flicker rounded-full bg-gold" />
            </div>
            <h3 className="mt-5 font-display text-2xl text-sand">বুকিং নিশ্চিত</h3>
            <p className="mt-3 text-sm leading-relaxed text-sand/75">
              {receipt.artist} আপনার {d.event_type} অনুষ্ঠানে ({receipt.date} {receipt.time ? "• " + receipt.time : ""}) পরিবেশনা করবেন — চূড়ান্ত <span className="font-semibold text-gold">{bn(receipt.price)}</span>।
            </p>
            <p className="mt-3 text-xs leading-relaxed text-sand/55">
              রসিদ-নং <span className="text-gold">{receipt.code}</span> • ঠিকানা-কথা ও বাকি আয়োজন ফোনে মিলবে। পেমেন্ট পরে, হাতে-হাতে।
            </p>
            <button
              data-testid="booking-receipt-close"
              onClick={onClose}
              className="mt-6 w-full rounded-full bg-alpana py-3 text-sm font-semibold text-dusk transition-transform duration-300 hover:scale-[1.02]"
            >
              ঠিক আছে
            </button>
          </div>
        ) : reply ? (
          /* ---------- শিল্পীর জবাব ও দর-কথা ---------- */
          <div data-testid="artist-reply">
            <p className="text-[11px] tracking-[0.25em] text-gold/70">শিল্পীর জবাব</p>
            <h3 className="mt-2 font-display text-2xl text-sand">{artist.name}</h3>

            {waitingArtist ? (
              <div className="my-10 text-center" data-testid="artist-waiting">
                <span className="mx-auto block h-4 w-4 animate-flicker rounded-full bg-vermilion" />
                <p className="mt-3 text-sm text-sand/75">শিল্পীর খবর আসছে…</p>
                <p className="mt-1 text-[11px] text-sand/45">প্রদীপের ছলছলে থামবেন না</p>
              </div>
            ) : (
              <>
                <div
                  className={`mt-5 rounded-xl border p-4 text-sm leading-relaxed ${
                    reply.kind === "ready" || reply.kind === "accept"
                      ? "border-gold/40 bg-gold/10 text-sand"
                      : reply.kind === "declined" || reply.kind === "decline"
                        ? "border-vermilion/40 bg-vermilion/10 text-sand/90"
                        : "border-sand/25 bg-earthen/70 text-sand/90"
                  }`}
                  data-testid="artist-reply-message"
                >
                  {reply.message}
                </div>

                {(reply.kind === "accept" || reply.kind === "ready") && (
                  <button
                    data-testid="booking-confirm"
                    onClick={confirmBooking}
                    disabled={busy}
                    className="mt-6 w-full rounded-full bg-vermilion py-3 text-sm font-medium text-alpana transition-all duration-300 hover:bg-[#d84a35] disabled:opacity-50"
                  >
                    {busy ? "হচ্ছে…" : reply.kind === "ready" ? "বুকিং কনফার্ম করুন" : "রাজি — বুকিং কনফার্ম করুন"}
                  </button>
                )}

                {reply.kind === "counter" && (
                  <div className="mt-6 space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        data-testid="reply-accept"
                        onClick={() => respond("accept")}
                        disabled={busy}
                        className="rounded-full bg-alpana px-5 py-2.5 text-sm font-medium text-dusk transition-transform duration-300 hover:scale-105 disabled:opacity-50"
                      >
                        রাজি — {bn(reply.price)}
                      </button>
                      <button
                        data-testid="reply-counter-open"
                        onClick={() => setOfferOpen(true)}
                        disabled={busy}
                        className="rounded-full border border-gold/40 px-5 py-2.5 text-sm text-gold transition-colors hover:bg-gold hover:text-dusk disabled:opacity-50"
                      >
                        আমার দাম বলি
                      </button>
                      <button
                        data-testid="reply-decline"
                        onClick={() => respond("decline")}
                        disabled={busy}
                        className="rounded-full border border-sand/25 px-5 py-2.5 text-sm text-sand/70 transition-colors hover:border-vermilion hover:text-vermilion disabled:opacity-50"
                      >
                        বাদ দিন
                      </button>
                    </div>
                    {offerOpen && (
                      <div className="flex items-center gap-2 rounded-full border border-gold/25 bg-earthen/70 py-1.5 pl-4 pr-1.5">
                        <input
                          data-testid="reply-counter-input"
                          autoFocus
                          type="number"
                          inputMode="numeric"
                          value={offer}
                          onChange={(e) => setOffer(e.target.value)}
                          placeholder={`আপনার দাম — যেমন ${Math.round(reply.price * 0.9)}`}
                          className="min-w-0 flex-1 bg-transparent py-2 text-sm text-sand placeholder:text-sand/35 focus:outline-none"
                        />
                        <button
                          data-testid="reply-counter-send"
                          onClick={() => respond("counter", Number(offer))}
                          disabled={busy || !Number(offer)}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-vermilion text-alpana disabled:opacity-40"
                          aria-label="দাম পাঠান"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {reply.kind === "decline" && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    <button
                      data-testid="decline-retry"
                      onClick={() => { setOfferOpen(true); setOffer(String(BUDGETS[1].max)); }}
                      className="rounded-full border border-gold/40 px-5 py-2.5 text-sm text-gold transition-colors hover:bg-gold hover:text-dusk"
                    >
                      বাজেট বাড়িয়ে দেখি
                    </button>
                    <button
                      data-testid="decline-close"
                      onClick={onClose}
                      className="rounded-full border border-sand/25 px-5 py-2.5 text-sm text-sand/70"
                    >
                      বন্ধ করুন
                    </button>
                    {offerOpen && (
                      <div className="mt-2 flex w-full items-center gap-2 rounded-full border border-gold/25 bg-earthen/70 py-1.5 pl-4 pr-1.5">
                        <input
                          data-testid="decline-counter-input"
                          autoFocus
                          type="number"
                          value={offer}
                          onChange={(e) => setOffer(e.target.value)}
                          placeholder="আপনার দাম লিখুন"
                          className="min-w-0 flex-1 bg-transparent py-2 text-sm text-sand placeholder:text-sand/35 focus:outline-none"
                        />
                        <button
                          data-testid="decline-counter-send"
                          onClick={() => respond("counter", Number(offer))}
                          disabled={busy || !Number(offer)}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-vermilion text-alpana disabled:opacity-40"
                          aria-label="দাম পাঠান"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            {error && <p className="mt-3 text-xs text-vermilion" data-testid="booking-error">{error}</p>}
          </div>
        ) : (
          /* ---------- ৮ ধাপ + হাজিরা ---------- */
          <div>
            <p className="text-[11px] tracking-[0.25em] text-gold/70">আমার অনুষ্ঠানে শিল্পী</p>
            <h3 className="mt-2 font-display text-2xl text-sand">{artist.name}</h3>
            <p className="mt-1 text-xs text-sand/55">{artist.art_form} • {artist.price_display} • অনুষ্ঠানে আসবেন, আপনার ঘরে-উঠানে</p>

            {/* ধাপের দাগ */}
            <div className="mt-5 flex items-center gap-1.5">
              {STEP_TITLES.map((_, i) => (
                <span key={i} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-gold" : "bg-sand/15"}`} />
              ))}
            </div>

            {step <= 7 ? (
              <div className="mt-6" data-testid={`booking-step-${step}`}>
                <h4 className="font-display text-xl text-sand">{STEP_TITLES[step]}</h4>

                {step === 0 && (
                  <>
                    <p className="mt-1 text-xs text-sand/50">যেমন — "আমার মেয়ের বিয়েতে বাউল চাই।"</p>
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="booking-event-options">
                      {EVENT_TYPES.map((t) => (
                        <button key={t} data-testid={`book-event-${t}`} onClick={() => pick("event_type", t)} className={chipCls(d.event_type === t)}>{t}</button>
                      ))}
                    </div>
                  </>
                )}

                {step === 1 && (
                  <div className="mt-4">
                    <input
                      data-testid="book-date"
                      type="date"
                      value={d.date}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setD((x) => ({ ...x, date: e.target.value }))}
                      className="w-full rounded-xl border border-gold/25 bg-earthen/70 px-4 py-3 text-sm text-sand focus:border-gold/60 focus:outline-none"
                    />
                    <p className="mt-2 text-[11px] text-sand/45">শিল্পী সাধারণত {artist.notice_days} দিনের নোটিশে আসেন।</p>
                  </div>
                )}

                {step === 2 && (
                  <div className="mt-4">
                    <input
                      data-testid="book-time"
                      type="time"
                      value={d.time}
                      onChange={(e) => setD((x) => ({ ...x, time: e.target.value }))}
                      className="w-full rounded-xl border border-gold/25 bg-earthen/70 px-4 py-3 text-sm text-sand focus:border-gold/60 focus:outline-none"
                    />
                    <p className="mt-2 text-[11px] text-sand/45">সন্ধ্যার আলোয় বাউল-বসন সবচেয়ে মানায়।</p>
                  </div>
                )}

                {step === 3 && (
                  <input
                    data-testid="book-location"
                    value={d.location}
                    onChange={(e) => setD((x) => ({ ...x, location: e.target.value }))}
                    placeholder="গ্রাম/শহর, জেলা — যেমন শান্তিনিকেতন, বীরভূম"
                    className="mt-4 w-full rounded-xl border border-gold/25 bg-earthen/70 px-4 py-3 text-sm text-sand placeholder:text-sand/30 focus:border-gold/60 focus:outline-none"
                  />
                )}

                {step === 4 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {DURATIONS.map((t) => (
                      <button key={t} data-testid={`book-duration-${t}`} onClick={() => pick("duration", t)} className={chipCls(d.duration === t)}>{t}</button>
                    ))}
                  </div>
                )}

                {step === 5 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {AUDIENCES.map((t) => (
                      <button key={t} data-testid={`book-audience-${t}`} onClick={() => pick("audience", t)} className={chipCls(d.audience === t)}>{t} জন</button>
                    ))}
                  </div>
                )}

                {step === 6 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {BUDGETS.map((b) => (
                      <button
                        key={b.label}
                        data-testid={`book-budget-${b.min}`}
                        onClick={() => setD((x) => ({ ...x, budget_min: b.min, budget_max: b.max }))}
                        className={chipCls(d.budget_min === b.min)}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                )}

                {step === 7 && (
                  <div className="mt-4 space-y-3">
                    <textarea
                      data-testid="book-note"
                      value={d.note}
                      onChange={(e) => setD((x) => ({ ...x, note: e.target.value }))}
                      placeholder="বিশেষ কিছু? — কোনো গান বা ঘরানা, বাজির সময়…"
                      rows={2}
                      className="w-full resize-none rounded-xl border border-gold/25 bg-earthen/70 px-4 py-3 text-sm text-sand placeholder:text-sand/30 focus:border-gold/60 focus:outline-none"
                    />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input
                        data-testid="book-name"
                        value={d.name}
                        onChange={(e) => setD((x) => ({ ...x, name: e.target.value }))}
                        placeholder="আপনার নাম"
                        className="w-full rounded-xl border border-gold/25 bg-earthen/70 px-4 py-3 text-sm text-sand placeholder:text-sand/30 focus:border-gold/60 focus:outline-none"
                      />
                      <input
                        data-testid="book-phone"
                        value={d.phone}
                        onChange={(e) => setD((x) => ({ ...x, phone: e.target.value }))}
                        placeholder="ফোন নম্বর"
                        inputMode="tel"
                        className="w-full rounded-xl border border-gold/25 bg-earthen/70 px-4 py-3 text-sm text-sand placeholder:text-sand/30 focus:border-gold/60 focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                <div className="mt-6 flex items-center justify-between gap-3">
                  <button
                    data-testid="booking-back"
                    onClick={() => { setAvail(null); setStep((s) => Math.max(0, s - 1)); }}
                    className="rounded-full border border-sand/25 px-4 py-2 text-xs text-sand/70 transition-colors hover:border-gold hover:text-gold"
                  >
                    ← আগের ধাপ
                  </button>
                  {step === 7 ? (
                    <button
                      data-testid="booking-check-availability"
                      onClick={checkAvailability}
                      disabled={!canNext || checking}
                      className="rounded-full bg-vermilion px-6 py-2.5 text-sm font-medium text-alpana shadow-[0_10px_30px_rgba(200,62,43,0.35)] transition-all duration-300 hover:bg-[#d84a35] disabled:opacity-40"
                    >
                      {checking ? "দেখা হচ্ছে…" : "শিল্পীর হাতে তারিখ দেখুন"}
                    </button>
                  ) : (
                    <button
                      data-testid="booking-next"
                      onClick={() => setStep((s) => s + 1)}
                      disabled={!canNext}
                      className="rounded-full bg-alpana px-6 py-2.5 text-sm font-semibold text-dusk transition-transform duration-300 hover:scale-105 disabled:opacity-40"
                    >
                      পরের ধাপ →
                    </button>
                  )}
                </div>
              </div>
            ) : step === 8 ? (
              /* ---------- হাজিরা ---------- */
              <div className="mt-6" data-testid="booking-availability">
                <h4 className="font-display text-xl text-sand">শিল্পীর হাতে তারিখ দেখা</h4>
                {checking ? (
                  <div className="my-10 text-center">
                    <span className="mx-auto block h-4 w-4 animate-flicker rounded-full bg-vermilion" />
                    <p className="mt-3 text-sm text-sand/75">দেখা হচ্ছে…</p>
                  </div>
                ) : !avail ? (
                  <div className="mt-6 text-center">
                    <p className="text-sm text-sand/70">যাচাই হলো না — আবার চেষ্টা করুন।</p>
                    <button data-testid="booking-availability-retry" onClick={checkAvailability} className="mt-3 rounded-full bg-vermilion px-6 py-2.5 text-sm text-alpana">
                      শিল্পীর হাতে তারিখ দেখুন
                    </button>
                  </div>
                ) : (
                  <>
                    <div
                      className={`mt-5 rounded-xl border p-5 text-center ${avail.available ? "border-gold/40 bg-gold/10" : "border-vermilion/40 bg-vermilion/10"}`}
                      data-testid={avail.available ? "book-available" : "book-unavailable"}
                    >
                      <p className="font-display text-lg text-sand">
                        {avail.available ? "আপনার তারিখে শিল্পী ফাঁকা আছেন" : "এই তারিখে সম্ভব নয়"}
                      </p>
                      <p className="mt-1 text-xs text-sand/60">{avail.reason}</p>
                    </div>
                    {avail.available && (
                      <button
                        data-testid="booking-send-request"
                        onClick={sendRequest}
                        disabled={sending}
                        className="mt-6 w-full rounded-full bg-vermilion py-3.5 text-sm font-medium text-alpana shadow-[0_10px_30px_rgba(200,62,43,0.35)] transition-all duration-300 hover:bg-[#d84a35] disabled:opacity-50"
                      >
                        {sending ? "পাঠাচ্ছি…" : "বুকিং-অনুরোধ পাঠান"}
                      </button>
                    )}
                    <div className="mt-3 flex justify-between">
                      <button data-testid="booking-change-date" onClick={() => { setAvail(null); setStep(1); }} className="text-[11px] text-sand/50 underline-offset-2 hover:text-gold hover:underline">
                        তারিখ বদলাই
                      </button>
                      <button data-testid="booking-back-2" onClick={() => setStep(7)} className="text-[11px] text-sand/50 underline-offset-2 hover:text-gold hover:underline">
                        ← আগের ধাপ
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : null}

            {sending && (
              <div className="mt-6 rounded-xl border border-gold/25 bg-earthen/70 p-4 text-center" data-testid="booking-sending">
                <span className="mx-auto block h-3 w-3 animate-flicker rounded-full bg-vermilion" />
                <p className="mt-2 text-sm text-sand/80">অনুরোধ যাচ্ছে — শিল্পীর খবর অপেক্ষা করুন…</p>
              </div>
            )}
            {error && !reply && <p className="mt-3 text-xs text-vermilion" data-testid="booking-step-error">{error}</p>}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
